import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wallet, ChevronDown, ChevronRight,
  Download, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";
import { MyReceipts } from "@/components/MyReceipts";

const eur = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  return `€${Number(n).toFixed(2)}`;
};

const vatLabel = (r) => {
  const t = String(r._tab || r.tab || r._model || r.model || "").toUpperCase();
  if (t.includes("6535") || t.includes("65_35")) return "PDV";
  if (t.includes("20")) return "PDV (20%)";
  if (t.includes("13")) return "PDV (13%)";
  return "PDV";
};

const modelLabel = (r) => {
  const t = String(r._tab || r.tab || r._model || r.model || "").toUpperCase();
  if (t.includes("6535") || t.includes("65_35")) return "Podjela 65 / 35";
  if (t.includes("20")) return "Paušal 20% + 5%";
  if (t.includes("13")) return "Paušal 13% + 5%";
  return "";
};

const SectionDivider = ({ label, testId }) => (
  <div className="flex items-center gap-3 pt-2" data-testid={testId}>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C5A059]/30 to-[#C5A059]/30" />
    <span className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]/80 whitespace-nowrap">{label}</span>
    <div className="h-px flex-1 bg-gradient-to-l from-transparent via-[#C5A059]/30 to-[#C5A059]/30" />
  </div>
);

/**
 * iter75.3 — Weekly Payout & Earnings History (driver-facing).
 *
 * Pulls every payout row for this driver from the master Google Sheet,
 * enriches each with the local fuel-receipt summary + refund, and
 * shows a transparent per-week breakdown.
 */
export default function DriverHistory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!localStorage.getItem("juke_driver_token")) {
      navigate("/driver");
      return;
    }
    driverApi.get("/driver/payouts/sheets/history")
      .then(({ data }) => setRows(data?.rows || []))
      .catch((err) => {
        if (err?.response?.status === 401) return navigate("/driver");
        toast.error(err?.response?.data?.detail || "Povijest nije moguće učitati.");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const downloadPdf = async (r) => {
    try {
      const resp = await driverApi.get("/driver/payouts/sheets/pdf", {
        params: { period: r.period, tab: r._tab },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error("PDF nije moguće otvoriti.");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="driver-history-page">
      <div className="border-b border-white/5 bg-black/60 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/driver/dashboard" className="text-zinc-400 hover:text-white transition" data-testid="history-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#C5A059]" />
              <h1 className="text-lg font-light">Tjedna zarada i isplate</h1>
            </div>
            <p className="text-[11px] text-zinc-500">Sinkronizirano s glavnom tablicom</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {loading ? (
          <div className="text-center text-zinc-500 py-16" data-testid="history-loading">Učitavanje…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-zinc-500 py-16" data-testid="history-empty">
            Još nema podataka o isplatama — pogledajte nakon prvog zatvorenog tjedna.
          </div>
        ) : (
          <div className="space-y-3">
            <SectionDivider label="Mjesečno · Gorivo i troškovi" testId="divider-monthly-fuel" />
            <MyReceipts />
            <SectionDivider label="Tjedno · Zarada i isplate" testId="divider-weekly-earnings" />
            <EarningsChart rows={rows} />
            {rows.map((r, i) => {
              const open = !!expanded[i];
              return (
                <div key={`${r.period}-${i}`} className="rounded-2xl border border-white/5 bg-[#0a0a0a] overflow-hidden"
                     data-testid={`history-week-${r.period}`}>
                  <button
                    onClick={() => setExpanded({ ...expanded, [i]: !open })}
                    className="w-full flex items-center gap-3 p-4 hover:bg-white/[0.02] transition text-left"
                  >
                    {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{r.period}</div>
                      <div className="text-white font-light text-lg mt-0.5">{eur(r.final_payout)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Isplata</div>
                      <div className="text-[#C5A059] font-semibold">{eur(r.final_payout)}</div>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-white/5 p-4 space-y-3 text-sm">
                      <SummaryRow k="Isplata" v={eur(r.final_payout)} highlight />
                      {modelLabel(r) && <SummaryRow k="Model naknade" v={modelLabel(r)} />}
                      <SummaryRow k="Ukupni neto promet" v={eur(r.total_turnover)} />
                      <SummaryRow k="Ukupni bruto promet" v={eur(r.gross_turnover)} />
                      <SummaryRow k={vatLabel(r)} v={eur(r.vat)} />
                      <SummaryRow k="Provizija tvrtke (5%)" v={eur(r.company_commission)} />
                      <SummaryRow k="Tjedni doprinosi" v={eur(r.contributions)} />
                      <SummaryRow k="Ukupna gotovina" v={eur(r.total_cash)} />
                      <SummaryRow k="Predana gotovina" v={eur(r.brought_cash)} />

                      {r.direct?.has_any && (
                        <div className="border-t border-white/5 pt-3" data-testid={`history-direct-${r.period}`}>
                          <div className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-2">
                            Direktne privatne vožnje
                          </div>
                          <SummaryRow k={`Bruto promet · ${r.direct.count} ${r.direct.count === 1 ? "vožnja" : "vožnji"}`} v={eur(r.direct.gross)} />
                          <SummaryRow k="Neto osnovica" v={eur(r.direct.net_base)} />
                          {r.direct.vat > 0 && <SummaryRow k="PDV (izlazni)" v={eur(r.direct.vat)} />}
                          <SummaryRow k="Udio vozača" v={eur(r.direct.driver_share)} />
                          <SummaryRow k="− Zadržana gotovina u vozilu" v={`− ${eur(r.direct.cash_retained)}`} />
                          <SummaryRow k="Neto za isplatu (direktne)" v={eur(r.direct.net_driver_payout)} />
                        </div>
                      )}

                      {r.b2b?.has_any && (
                        <div className="border-t border-white/5 pt-3" data-testid={`history-b2b-${r.period}`}>
                          <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-2">
                            B2B Vožnje (Mjesečni R-1 račun)
                          </div>
                          <SummaryRow
                            k={`Na mjesečni račun · ${r.b2b.invoice_count} ${r.b2b.invoice_count === 1 ? "vožnja" : "vožnji"} (u bruto prometu)`}
                            v={eur(r.b2b.invoice_turnover)}
                          />
                          <SummaryRow
                            k={`Naplaćeno od gosta u vozilu · ${r.b2b.collect_count} ${r.b2b.collect_count === 1 ? "vožnja" : "vožnji"}`}
                            v={eur(r.b2b.collect_amount)}
                          />
                        </div>
                      )}

                      <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ukupno za isplatu</div>
                          <div className="text-[#C5A059] font-semibold text-lg">{eur(r.final_payout)}</div>
                        </div>
                        <button
                          onClick={() => downloadPdf(r)}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C5A059]/40 text-[#C5A059] text-xs hover:bg-[#C5A059]/10 transition"
                          data-testid={`history-pdf-${r.period}`}
                        >
                          <Download className="w-4 h-4" /> PDF isplate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ k, v, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-zinc-500 text-xs">{k}</div>
      <div className={highlight ? "text-[#C5A059] font-semibold" : "text-zinc-200"}>{v}</div>
    </div>
  );
}

function EarningsChart({ rows }) {
  const data = [...rows]
    .filter((r) => r.total_due != null || r.final_payout != null)
    .reverse()
    .slice(-12);
  if (data.length < 2) return null;
  const vals = data.map((r) => Number(r.total_due ?? r.final_payout) || 0);
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const W = 560, H = 120, PAD = 8;
  const x = (i) => PAD + (i * (W - 2 * PAD)) / (data.length - 1);
  const y = (v) => H - PAD - ((v - min) / (max - min || 1)) * (H - 2 * PAD);
  const points = vals.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  const area = `${PAD},${H - PAD} ${points} ${W - PAD},${H - PAD}`;
  const last = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const up = last >= prev;
  return (
    <div className="rounded-2xl border border-white/5 bg-[#0a0a0a] p-4" data-testid="earnings-chart">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Trend zarade · zadnjih {data.length} tjedana</div>
        <div className={`text-xs font-semibold ${up ? "text-emerald-400" : "text-red-400"}`}>
          {up ? "▲" : "▼"} {eur(last)}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="goldFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C5A059" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#goldFade)" />
        <polyline points={points} fill="none" stroke="#C5A059" strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round" />
        {vals.map((v, i) => (
          <circle key={i} cx={x(i)} cy={y(v)} r={i === vals.length - 1 ? 4 : 2.5}
            fill={i === vals.length - 1 ? "#D4AF37" : "#C5A059"} />
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        <span className="text-[9px] text-zinc-600">{data[0]?.period}</span>
        <span className="text-[9px] text-zinc-600">{data[data.length - 1]?.period}</span>
      </div>
    </div>
  );
}
